import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, RefreshCw, Loader2, Activity, MessagesSquare, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const LINES = [
  { key: "etat", label: "État du dossier", icon: Activity },
  { key: "echanges", label: "Derniers échanges", icon: MessagesSquare },
  { key: "prochaine_action", label: "Prochaine action", icon: ArrowRightCircle, strong: true },
];

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function DossierAiSummary({ dossierId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = useCallback(async (silent) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/dossiers/${dossierId}/ai-summary`);
      setData(d);
      if (!silent) toast.success("Synthèse IA mise à jour");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Synthèse IA indisponible");
    } finally {
      setBusy(false);
    }
  }, [dossierId]);

  useEffect(() => {
    let alive = true;
    api.get(`/dossiers/${dossierId}/ai-summary`).then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      if (!d.summary || d.stale) generate(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [dossierId, generate]);

  const s = data?.summary;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-5 py-4" data-testid="dossier-ai-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-emerald-900">
          <Sparkles className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-wider font-semibold">Synthèse IA</span>
          {data?.generated_at && (
            <span className="text-[11px] text-emerald-800/70" data-testid="ai-summary-generated-at">· mise à jour {fmt(data.generated_at)}</span>
          )}
        </div>
        <button
          onClick={() => generate(false)}
          disabled={busy}
          className="text-xs px-2.5 py-1 rounded border border-emerald-300 bg-white/70 text-emerald-900 hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="ai-summary-regenerate-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Régénérer
        </button>
      </div>
      {!s ? (
        <div className="text-sm text-emerald-900/80 inline-flex items-center gap-2" data-testid="ai-summary-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Analyse du dossier en cours…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LINES.map(({ key, label, icon: Icon, strong }) => (
            <div key={key} className="min-w-0" data-testid={`ai-summary-${key}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-800/80 mb-1">
                <Icon className="w-3.5 h-3.5" /> {label}
              </div>
              <div className={`text-sm leading-snug ${strong ? "font-medium text-emerald-950" : "text-emerald-950/90"}`}>{s[key] || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
